import { gql, useQuery } from '@apollo/client';
import { validateZipCode } from '@arcadiapower/warbler';
import { useEffect, useMemo } from 'react';
import { LDClient, LDFlags } from '~/providers';
import { Flex_FetchUtilitiesQuery, Flex_FetchUtilitiesQueryVariables } from '~/typings/graphql';
import { ArrayElementType } from '~/typings/utils';
import { getStyledSystemProps } from '~/utils/styled-system';
import { StyledSelect, StyledSelectProps } from '../select.style';

export const FETCH_UTILITIES = gql`
  query Flex_FetchUtilities($zipCode: String!) {
    utilities: utilitiesByZip(zipCode: $zipCode) {
      suggested {
        id
        name: displayName
      }
      other {
        id
        name: displayName
      }
    }
  }
`;

type UtilityData = {
  id: number;
  name: string;
} | null;

type UtilityType = NonNullable<
  ArrayElementType<Flex_FetchUtilitiesQuery['utilities']['suggested']>
>;
type Option = { text: string; value: number };

const mapOptions = (utility: UtilityType): Option => ({
  text: utility.name,
  value: utility.id,
});

const moveOtherToEnd = (firstOption: Option, secondOption: Option): number => {
  if (firstOption.text?.includes('Other')) return 1;
  if (secondOption.text?.includes('Other')) return -1;
  return 0;
};

interface Props extends StyledSelectProps {
  onChange: (zip: number, { isValid, name }: { isValid: boolean; name: 'utilityId' }) => void;
  value?: number;
  zipCode: string;
}

export const Utility = ({ onChange, value, zipCode, ...rest }: Props) => {
  const { data, loading } = useQuery<Flex_FetchUtilitiesQuery, Flex_FetchUtilitiesQueryVariables>(
    FETCH_UTILITIES,
    {
      skip: !validateZipCode(zipCode),
      variables: { zipCode },
    },
  );

  const { suggested, other } = useMemo(() => {
    const filterUtilities = (utilities: UtilityData[]) => {
      if (!LDClient.variation(LDFlags.enableUnitilMA))
        return utilities?.filter(utility => utility?.name !== 'Unitil MA');
      return utilities;
    };

    // removed filter for Unitil MA to test
    return {
      suggested: filterUtilities(data?.utilities?.suggested || []),
      other: filterUtilities(data?.utilities?.other || []),
    };
  }, [data?.utilities]);
  const availableUtilities = !!(other?.length || suggested?.length);

  const handleChange = (id: number | string) => {
    onChange(Number(id), { isValid: !!id, name: 'utilityId' });
  };

  useEffect(() => {
    if (
      (suggested.length || other.length) &&
      ![...suggested, ...other].find((u: UtilityType) => u.id === value)
    ) {
      if (suggested.length) {
        handleChange(suggested[0]!.id);
      } else {
        handleChange(other[0]!.id);
      }
    } else if (value) {
      handleChange(value);
    }
  }, [suggested]);

  return (
    <StyledSelect
      {...getStyledSystemProps(rest)}
      disabled={!availableUtilities}
      id="utility-select"
      label="Select your electric utility"
      loading={loading}
      minHeight="auto"
      name="utilityId"
      onChange={handleChange}
      options={other?.map(mapOptions).sort(moveOtherToEnd) || []}
      suggestedOptions={suggested?.map(mapOptions) || []}
      value={value ? String(value) : ''}
    />
  );
};
